/**
 * Slice modal to bulk-add related opportunities/stories with compact input rows.
 * Also exposes an AI suggestion button that calls the backend for LLM-powered slicing.
 */
import { useState, useEffect } from 'react';
import { suggestSlices } from '../../api/ai';
import { useStore } from 'store/useStore';
import { AiSliceSet, InsightNode, NodeType, OstNode } from 'types';
import { useFeatureAccess, handleAiLimitReached } from 'pricing';
import { Modal, Button, FormRow, TwoCol, Divider, IconButton } from '../ui';

/** A node that shares a soft (drag-connect) connection with the node being sliced. */
type ConnectedNodeRef = { direction: 'incoming' | 'outgoing'; node: OstNode };

const MOSCOW_OPTS = ['Must have', 'Should have', 'Could have', "Won't have (MVP)"] as const;
const STATUS_OPTS = ['Draft', 'Ready to build', 'Pushed to GitHub', 'Done'] as const;

// The Status dropdown is temporarily disabled here too (see NodePanel.tsx's
// SHOW_NODE_STATUS_DROPDOWN) — gated behind a flag rather than deleted so the field, its options,
// and the `status` value on each row are kept intact for a later refactor/repurposing. New rows
// still default to 'Draft' via defaultRow below. Flip back to true to restore the control.
const SHOW_STATUS_DROPDOWN = false;

type SlicePanelProps = {
    type: NodeType;
    parentNode: OstNode;
    onClose: () => void;
    onCreate: (rows: SliceRow[]) => void;
    /** Root outcome title used to keep AI suggestions goal-aligned. */
    outcomeTitle?: string;
    /** Full ancestor chain from root down to (but not including) the node being sliced. */
    ancestorNodes?: OstNode[];
    /** Insight nodes linked to any node in the direct hierarchy (ancestors + the node being sliced). */
    hierarchyInsights?: InsightNode[];
    /**
     * Nodes soft-connected to the slicing target via the drag-connect mechanism.
     * Includes both incoming (other node → target) and outgoing (target → other node) directions.
     */
    connectedNodes?: ConnectedNodeRef[];
    /** Called when AI suggestions arrive; hands the slice sets and model name up to the Canvas. */
    onAiSuggest?: (sets: AiSliceSet[], aiModel?: string) => void;
};

type SliceRow = {
    title: string;
    description: string;
    moscow: (typeof MOSCOW_OPTS)[number];
    status: (typeof STATUS_OPTS)[number];
};

export default function SlicePanel({ type, parentNode, onClose, onCreate, outcomeTitle, ancestorNodes, hierarchyInsights, connectedNodes, onAiSuggest }: SlicePanelProps) {
    const defaultRow: SliceRow = { title: '', description: '', moscow: 'Could have', status: 'Draft' };
    const [rows, setRows] = useState<SliceRow[]>([{ ...defaultRow }]);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState<string | null>(null);

    // Read user-configured suggestion count and model quality from settings (1–10, default 3)
    const { aiSuggestionCount, aiModelQuality } = useStore();

    // Gate the AI slicing feature — free-tier users see the button but get an
    // upgrade prompt instead of triggering the API call.
    const { tryAccess: tryAiAccess } = useFeatureAccess('ai_slicing');

    const setRow = (idx: number, key: keyof SliceRow, value: string) => {
        setRows((old) =>
            old.map((r, i) =>
                i === idx
                    ? {
                        ...r,
                        [key]: value,
                    }
                    : r,
            ),
        );
    };

    const addRowItem = () => setRows((old) => [...old, { ...defaultRow }]);
    const removeRow = (idx: number) => setRows((old) => old.filter((_, i) => i !== idx));

    // While AI is loading, intercept Escape so the modal cannot be dismissed
    // mid-request, which would waste the user's API credits.
    useEffect(() => {
        if (!aiLoading) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                e.preventDefault();
            }
        };
        window.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
    }, [aiLoading]);

    /**
     * Calls the AI backend for slice suggestions, then hands the result up to the
     * Canvas via onAiSuggest. The panel is closed so the ghost-node preview can begin.
     */
    const handleAiSuggest = async () => {
        // Check pricing access first — shows upgrade toast if locked and returns false.
        if (!tryAiAccess()) return;
        if (!onAiSuggest) return;
        setAiLoading(true);
        setAiError(null);

        const nodeType = type === 'opportunity' ? 'opportunity' : 'story';
        // Pass childType so the server generates the correct structured output schema:
        // story → asA/iWant/soThat/acceptanceCriteria; experiment → assumption/method/successMetric.
        const childType: 'story' | 'experiment' = type === 'experiment' ? 'experiment' : 'story';
        const result = await suggestSlices({
            nodeType,
            childType,
            parentTitle: parentNode.title,
            parentDescription: parentNode.description,
            outcomeTitle,
            modelQuality: aiModelQuality,
            count: aiSuggestionCount,
            ancestorNodes: ancestorNodes?.map((n) => ({
                type: n.type,
                title: n.title,
                description: n.description,
            })),
            hierarchyInsights: hierarchyInsights?.map((insight) => ({
                title: insight.title,
                description: insight.description,
            })),
            connectedNodes: connectedNodes?.map((ref) => ({
                direction: ref.direction,
                type: ref.node.type,
                title: ref.node.title,
                description: ref.node.description,
            })),
        });

        setAiLoading(false);
        useStore.getState().refreshAiUsage();

        if ('error' in result) {
            if (!handleAiLimitReached(result)) setAiError(result.error);
            return;
        }

        // Hand sets up and close — Canvas takes over with ghost-node preview
        onAiSuggest(result.sliceSets, result.aiModel);
        onClose();
    };

    /**
     * Validate rows and send create request, then close panel.
     */
    const create = () => {
        const valid = rows.filter((r) => r.title.trim());
        if (valid.length === 0) {
            alert('Please add at least one slice with a title');
            return;
        }
        onCreate(valid);
        onClose();
    };

    const headerTitle = parentNode.type === 'solution' ? `Slice Solution into ${type}s` : `Slice ${type}`;

    return (
        <Modal
            title={headerTitle}
            onClose={onClose}
            headerVariant={parentNode.type === 'solution' ? 'solution' : type}
            closeDisabled={aiLoading}
            closeOnOverlayClick={!aiLoading}
        >
            {/* AI suggestion trigger */}
            {onAiSuggest && (() => {
                const titleLen = (parentNode.title || '').trim().length;
                const descLen = (parentNode.description || '').trim().length;
                const weakTitle = titleLen < 10;
                const noDesc = descLen === 0;
                const sparseDesc = descLen > 0 && descLen < 30;

                // Solution nodes are treated as Epics; the user is assumed to have
                // provided enough description to generate meaningful slices.
                const isSolutionEpic = parentNode.type === 'solution';
                const showWarning = !isSolutionEpic && (weakTitle || noDesc || sparseDesc);

                const warningText = weakTitle
                    ? 'The node title is very short. Add more context for better suggestions.'
                    : noDesc
                        ? 'No description provided. Suggestions may be generic without more context.'
                        : 'The description is brief. More detail will produce more relevant suggestions.';

                return (
                    <div className='slice-ai-section'>
                        <button
                            className='btn btn-ai'
                            onClick={handleAiSuggest}
                            disabled={aiLoading}
                        >
                            {aiLoading ? '⏳ Generating suggestions…' : '✨ Suggest with AI'}
                        </button>
                        {aiError && <p className='slice-ai-error'>{aiError}</p>}
                        {!aiError && !aiLoading && showWarning && (
                            <p className='slice-ai-warning'>⚠ {warningText}</p>
                        )}
                        {!aiError && !aiLoading && !showWarning && (
                            <p className='slice-ai-hint'>
                                {isSolutionEpic
                                    ? `AI will treat this Solution as an Epic and generate ${aiSuggestionCount} set${aiSuggestionCount !== 1 ? 's' : ''} of ${type} slices. Use ↹ to cycle, Enter to accept.`
                                    : 'AI will generate 3 slicing lenses. Use ↹ to cycle, Enter to accept.'}
                            </p>
                        )}
                    </div>
                );
            })()}

            <Divider>or add manually</Divider>
            {rows.map((row, idx) => (
                <div key={idx} className='slice-row'>
                    <div className='slice-row-header'>
                        <span className='slice-row-number'>Item {idx + 1}</span>
                        {rows.length > 1 && (
                            <IconButton onClick={() => removeRow(idx)} disabled={aiLoading}>
                                ✕
                            </IconButton>
                        )}
                    </div>
                    <FormRow>
                        <label className='form-label'>Title *</label>
                        <input
                            className='form-input'
                            value={row.title}
                            onChange={(e) => setRow(idx, 'title', e.target.value)}
                            placeholder={`${type} title`}
                            disabled={aiLoading}
                        />
                    </FormRow>
                    <FormRow>
                        <label className='form-label'>Description</label>
                        <textarea
                            className='form-textarea form-textarea-h56'
                            value={row.description}
                            onChange={(e) => setRow(idx, 'description', e.target.value)}
                            disabled={aiLoading}
                        />
                    </FormRow>
                    {/* Both story and experiment share the StoryNode type with moscow/status fields. */}
                    {(type === 'story' || type === 'experiment') && (() => {
                        const moscowField = (
                            <FormRow>
                                <label className='form-label'>MoSCoW</label>
                                <select
                                    className='form-select'
                                    value={row.moscow}
                                    onChange={(e) => setRow(idx, 'moscow', e.target.value)}
                                    disabled={aiLoading}
                                >
                                    {MOSCOW_OPTS.map((o) => (
                                        <option key={o}>{o}</option>
                                    ))}
                                </select>
                            </FormRow>
                        );

                        if (!SHOW_STATUS_DROPDOWN) return moscowField;

                        return (
                            <TwoCol>
                                {moscowField}
                                <FormRow>
                                    <label className='form-label'>Status</label>
                                    <select
                                        className='form-select'
                                        value={row.status}
                                        onChange={(e) => setRow(idx, 'status', e.target.value)}
                                        disabled={aiLoading}
                                    >
                                        {STATUS_OPTS.map((o) => (
                                            <option key={o}>{o}</option>
                                        ))}
                                    </select>
                                </FormRow>
                            </TwoCol>
                        );
                    })()}
                </div>
            ))}

            <div className='modal-actions'>
                <Button variant='secondary' onClick={addRowItem} disabled={aiLoading}>
                    + Add another
                </Button>
                <Button variant='primary' onClick={create} disabled={aiLoading}>
                    Create slices
                </Button>
            </div>
        </Modal>
    );
}
